import { clampPageSize } from 'v2/api-console/shared/utils/pagination-utils'
import { dateRangeToUtc } from 'v2/api-console/shared/utils/recording-filter-utils'

/**
 * Plivo's Transcription API caps `limit` at 20 — asking for more silently returns 20, so
 * clamp here rather than letting the table believe it asked for a bigger page.
 */
export const TRANSCRIPTIONS_PAGE_SIZE = 20

/**
 * Build the upstream query for `GET /v1/Account/{authId}/Transcription/` from the table's
 * filter params. Kept as a pure function (timezone passed in, never read from the shared
 * queryClient) so it is testable without mock.module — see date-filter-params.test.ts for
 * why touching the org-timezone getter in a test makes the suite order-dependent.
 *
 * Two upstream quirks it exists to absorb:
 *  - `add_time__gte`/`__lte` are compared against the UTC instant and only accept
 *    'YYYY-MM-DD HH:mm:ss'; a bare 'YYYY-MM-DD' is rejected with a format error. The
 *    response echoes `add_time` back in account tz, so the two are not interchangeable.
 *  - `limit` silently caps at 20.
 */
export const buildTranscriptionListParams = (
  params: { callUuid?: string } & Record<string, unknown>,
  tz: string,
): Record<string, unknown> => {
  const { callUuid, ...rest } = params
  const out: Record<string, unknown> = { ...rest }

  if (callUuid) out.call_uuid = callUuid

  // Switching over from the Recordings tab leaves its `subaccount` param in the URL, and
  // this endpoint SILENTLY IGNORES it (results come back unfiltered), so forwarding it
  // would imply a subaccount scope we cannot honour. Drop it, matching what
  // listCallRecordings does before it calls plivo-api.
  delete out.subaccount

  if (out.limit !== undefined) {
    out.limit = String(clampPageSize(out.limit, TRANSCRIPTIONS_PAGE_SIZE))
  }

  if (typeof out.add_time === 'string') {
    const { gte, lte } = dateRangeToUtc(out.add_time, tz)
    delete out.add_time
    if (gte) out.add_time__gte = gte
    if (lte) out.add_time__lte = lte
  }

  return out
}
